import { Link } from 'react-router-dom';
import {
  Zap, Droplets, BrickWall, Paintbrush, Snowflake, Layers, Network, Wrench,
  PiggyBank, TrendingUp, ShieldCheck, Leaf, Radar, ArrowRight, MessageCircle, Timer, ClipboardCheck,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { FadeUp } from '../components/Reveal';
import { pmp, waUrl, images } from '../data/site';

const COVERAGE_ICONS = {
  zap: Zap, droplets: Droplets, brick: BrickWall, paintbrush: Paintbrush,
  snowflake: Snowflake, layers: Layers, network: Network, wrench: Wrench,
};

const BENEFIT_ICONS = {
  piggy: PiggyBank, trending: TrendingUp, shield: ShieldCheck, leaf: Leaf, radar: Radar,
};

const PMP_MESSAGE = 'Olá, Eng. Amilcar! Acessei o site do Grupo Majo e quero conhecer o PMP (Plano de Manutenção Preventiva) para a minha empresa.';

const MAPA_ATUAL = [
  'O condomínio informa à administradora que precisa de uma manutenção urgente na área comum.',
  'A administradora busca profissionais e agenda visitas — nem sempre no mesmo dia; o síndico fica impedido de tocar outras ações enquanto aguarda os prestadores.',
  'Os prestadores visitam o condomínio conforme agendamento — com o risco de não comparecerem, gerando insatisfação do síndico.',
  'Após as visitas, cada profissional gera um orçamento com soluções e preços muito divergentes entre si.',
  'A administradora recebe os orçamentos, analisa os descritivos e encaminha ao condomínio para aprovação.',
  'Os responsáveis no condomínio analisam formas de execução, preços e prazo — só a coleta dos orçamentos já leva, no mínimo, 3 dias.',
  'Se aprovado, a administradora agenda o início dos trabalhos com o profissional; se reprovado, o processo volta à busca por novos prestadores.',
  'Só então o reparo é realizado: entre a primeira visita e a aprovação, o processo pode levar de 15 a 30 dias úteis.',
];

const MAPA_PROPOSTO = [
  'O condomínio informa à administradora que precisa de uma manutenção urgente na área comum.',
  'A administradora aciona diretamente a Majo Engenharia e Manutenção, já contratada por contrato de manutenção predial.',
  'SLA de até 36 horas para visitar o local e reparar ocorrências de Nível I (descriminadas em contrato). A mão de obra já está incluída no atendimento; materiais indisponíveis no condomínio são cobrados à parte, na nota fiscal do mês.',
  'O condomínio volta à plena conformidade de uso: condôminos satisfeitos e nenhum stress para resolver casos de Nível I.',
];

const ORGANOGRAMA = [
  { area: 'Engenharia', items: ['Projetos', 'Elétrica', 'Hidráulica', 'Alvenaria', 'Pintura', 'Refrigeração'] },
  { area: 'Manutenção', items: ['Acompanhamento de Obras', 'Arquitetônico', 'Laudos', 'Perícias'] },
  { area: 'Licitações', items: ['Análise de Editais', 'Análise de Contratos', 'Propostas'] },
  { area: 'Administrativo', items: ['Compras', 'Financeiro', 'Contas a Pagar e Receber', 'Cobrança', 'Almoxarifado', 'RH', 'Jurídico', 'Comercial', 'Treinamento'] },
];

const SOLUCOES = [
  'Pintura', 'Hidráulica', 'Elétrica', 'Projetos', 'Reformas', 'Refrigeração / Ar-Condicionado',
  'Gesso / Drywall', 'TI / Redes', 'Manutenção Geral', 'Laudos de Engenharia', 'Vistoria de Recebimento',
  'Avaliação de Imóvel', 'Perícia de Imóvel', 'Acompanhamento de Obras', 'Responsabilidade Técnica',
  'Consultoria em Obras', 'Projetos Arquitetônicos',
];

const ATRIBUICOES = [
  'Gestão, supervisão, coordenação e orientação técnica em projetos.',
  'Assistência, assessoria e consultoria em engenharia civil.',
  'Vistoria, perícia, inspeção, avaliação, monitoramento, laudo, parecer técnico, auditoria e arbitragem.',
  'Coleta de dados, estudo, planejamento, anteprojeto, projeto, detalhamento, dimensionamento e especificação.',
];

export default function PmpEmpresas() {
  return (
    <>
      <PageHeader
        index="09"
        eyebrow={pmp.eyebrow}
        title={pmp.title}
        description={pmp.intro}
        image={images.structure}
      />

      <section className="relative overflow-hidden bg-navy py-16 text-white" data-testid="pmp-sla-band">
        <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
        <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-6 px-5 text-center lg:flex-row lg:justify-between lg:text-left lg:px-8">
          <FadeUp>
            <p className="flex items-center justify-center gap-3 font-display text-lg font-bold md:text-2xl lg:justify-start">
              <Timer size={26} className="shrink-0 text-gold" />
              {pmp.sla}
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-[0.18em] text-gold-light">
              <ClipboardCheck size={15} /> Conforme NBR 5674 — Manutenção em Edificações
            </span>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="pmp-maps">
        <SectionHeading
          eyebrow="Mapa de Atendimento"
          title="Sem a Majo: dias de espera. Com a Majo: reparo em horas."
          description="Comparativo fiel aos mapas da nossa apresentação comercial: o fluxo tradicional de contratação de prestadores versus o fluxo proposto com o PMP ativo."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <FadeUp>
            <article className="h-full rounded-3xl border border-red-400/30 surface p-7 md:p-9" data-testid="map-atual">
              <div className="flex items-center justify-between gap-4">
                <h3 className="flex items-center gap-2.5 font-display text-lg font-extrabold text-navy dark:text-white md:text-xl">
                  <AlertTriangle size={20} className="text-red-500" /> Hoje, sem o PMP
                </h3>
                <span className="rounded-full bg-red-500/10 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                  15 a 30 dias úteis
                </span>
              </div>
              <ol className="mt-8 space-y-5">
                {MAPA_ATUAL.map((step, i) => (
                  <li key={i} className="flex gap-4" data-testid={`map-atual-step-${i + 1}`}>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-red-400/40 bg-red-500/10 font-display text-xs font-extrabold text-red-600 dark:text-red-400">
                      {i + 1}
                    </span>
                    <p className="pt-1 text-sm leading-relaxed text-muted-c">{step}</p>
                  </li>
                ))}
              </ol>
            </article>
          </FadeUp>
          <FadeUp delay={0.12}>
            <article className="relative h-full overflow-hidden rounded-3xl bg-navy p-7 text-white md:p-9" data-testid="map-proposto">
              <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
              <div className="relative">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="flex items-center gap-2.5 font-display text-lg font-extrabold md:text-xl">
                    <CheckCircle2 size={20} className="text-gold" /> Com o PMP Majo
                  </h3>
                  <span className="rounded-full bg-gold/15 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-gold-light">
                    Até 36 horas
                  </span>
                </div>
                <ol className="mt-8 space-y-5">
                  {MAPA_PROPOSTO.map((step, i) => (
                    <li key={i} className="flex gap-4" data-testid={`map-proposto-step-${i + 1}`}>
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gold font-display text-xs font-extrabold text-navy-deep">
                        {i + 1}
                      </span>
                      <p className="pt-1 text-sm leading-relaxed text-white/80">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </article>
          </FadeUp>
        </div>
        <FadeUp delay={0.2}>
          <p className="mt-8 text-center text-xs text-muted-c" data-testid="maps-source-note">
            Reprodução digital dos mapas de atendimento da apresentação institucional do Grupo Majo (Rev. 03/2025).
          </p>
        </FadeUp>
      </section>

      <section className="border-y border-line bg-[var(--surface)]" data-testid="pmp-types">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Tipos de Manutenção"
            title="Planejamos o que pode ser previsto — e respondemos ao que não pode"
            description="O PMP combina rotinas planejadas de prevenção com resposta estruturada às ocorrências não planejadas."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pmp.types.map((t, i) => (
              <FadeUp key={t.name} delay={i * 0.08}>
                <article className="card-hover h-full rounded-2xl surface p-7" data-testid={`pmp-type-card-${i}`}>
                  <span
                    className={`chip ${
                      t.group === 'Manutenção Planejada'
                        ? '!border-gold/50 !bg-gold/10 !text-navy dark:!text-gold-light'
                        : ''
                    }`}
                  >
                    {t.group}
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold text-navy dark:text-white">{t.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-c">{t.text}</p>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="pmp-coverage">
        <SectionHeading
          eyebrow="Cobertura Técnica"
          title="Oito disciplinas em um único contrato"
          description="Elétrica, hidráulica, alvenaria, pintura, refrigeração, gesso e drywall, TI/redes e manutenção geral — sem precisar gerenciar múltiplos fornecedores."
        />
        <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-4">
          {pmp.coverage.map((c, i) => {
            const Icon = COVERAGE_ICONS[c.icon];
            return (
              <FadeUp key={c.label} delay={(i % 4) * 0.07}>
                <div
                  className="card-hover flex h-full flex-col items-center gap-3 rounded-2xl surface p-6 text-center"
                  data-testid={`pmp-coverage-${i}`}
                >
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-gold-light dark:bg-white/10">
                    <Icon size={22} strokeWidth={1.8} />
                  </span>
                  <span className="font-display text-sm font-bold leading-snug text-navy dark:text-white">{c.label}</span>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <section className="border-y border-line bg-[var(--surface)]" data-testid="pmp-structure">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Detalhamento dos Processos"
            title="Estrutura funcional, soluções e atribuições do Grupo Majo"
            description="O organograma e o escopo de atribuições apresentados na nossa apresentação institucional para empresas."
          />
          <FadeUp delay={0.1} className="mt-12 text-center">
            <span className="inline-block rounded-full bg-navy px-8 py-3 font-display text-sm font-extrabold uppercase tracking-[0.2em] text-gold-light" data-testid="org-root">
              Grupo Majo
            </span>
          </FadeUp>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {ORGANOGRAMA.map((o, i) => (
              <FadeUp key={o.area} delay={i * 0.08}>
                <article className="card-hover h-full rounded-2xl surface p-6" data-testid={`org-area-${i}`}>
                  <h3 className="border-b border-line pb-3 font-display text-sm font-extrabold uppercase tracking-[0.16em] text-azure dark:text-gold-light">
                    {o.area}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {o.items.map((item) => (
                      <span key={item} className="chip !normal-case !tracking-normal">{item}</span>
                    ))}
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <h3 className="font-display text-xl font-bold text-navy dark:text-white">Nossas Soluções</h3>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="pmp-solutions">
                {SOLUCOES.map((s) => (
                  <span key={s} className="chip !px-4 !py-2 !normal-case !tracking-normal">{s}</span>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.12}>
              <h3 className="font-display text-xl font-bold text-navy dark:text-white">Nossas Atribuições</h3>
              <ul className="mt-6 space-y-4" data-testid="pmp-attributions">
                {ATRIBUICOES.map((a, i) => (
                  <li key={a} className="flex gap-3.5">
                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gold/15 font-display text-[10px] font-extrabold text-navy dark:text-gold-light">
                      {i + 1}
                    </span>
                    <p className="text-sm leading-relaxed text-muted-c">{a}</p>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="pmp-steps">
        <SectionHeading
          eyebrow="Como Funciona"
          title="Do diagnóstico ao chamado atendido, em quatro movimentos"
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {pmp.steps.map((s, i) => (
            <FadeUp key={s.step} delay={i * 0.08}>
              <article className="card-hover flex h-full flex-col rounded-2xl surface p-7" data-testid={`pmp-step-${i}`}>
                <span className="font-display text-4xl font-extrabold outline-numeral" aria-hidden="true">{s.step}</span>
                <h3 className="mt-4 font-display text-base font-bold leading-snug text-navy dark:text-white">{s.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-c">{s.text}</p>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy py-24 text-white lg:py-28" data-testid="pmp-benefits">
        <div className="absolute inset-0 bg-blueprint" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            light
            eyebrow="Benefícios do PMP"
            title="Por que empresas adotam manutenção preventiva"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {pmp.benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[b.icon];
              return (
                <FadeUp key={b.title} delay={i * 0.07}>
                  <article
                    className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-gold/40"
                    data-testid={`pmp-benefit-${i}`}
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-gold/15 text-gold-light">
                      <Icon size={20} strokeWidth={1.8} />
                    </span>
                    <h3 className="mt-4 font-display text-sm font-bold leading-snug">{b.title}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-white/65">{b.text}</p>
                  </article>
                </FadeUp>
              );
            })}
          </div>
          <FadeUp delay={0.2} className="mt-14 flex flex-col items-center justify-center gap-3.5 sm:flex-row">
            <a
              href={waUrl(PMP_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pmp-whatsapp-cta"
              className="btn-gold"
            >
              <MessageCircle size={17} /> Quero o PMP na minha empresa
            </a>
            <Link to="/simulador" data-testid="pmp-simulator-cta" className="btn-outline text-white">
              Simular Orçamento <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
